import { ReactNode } from 'react';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import RetentionProgress from '../RetentionProgress.component';

export type tRetentionOfferShellProps = {
  isOpen: boolean;
  /** Eyebrow, heading and supporting line, centred above the card. */
  heading: string;
  intro: string;
  /** The card's header band: what is being offered, in one line. */
  cardIcon: typeof XMarkIcon;
  cardTitle: string;
  /** The terms of the offer, when they fit beside the title. */
  cardTerm?: string;
  /** The card's padded body — the price, the choices, the details. */
  children: ReactNode;
  /** A tinted band at the card's foot, for terms that must survive skimming. */
  cardFooter?: ReactNode;
  /** A line under the card, outside its border. */
  footnote?: ReactNode;
  onBack: () => void;
  onDecline: (event: React.FormEvent<HTMLFormElement>) => void;
  onAccept: () => void;
  acceptLabel: string;
  declineLabel: string;
  onClose: () => void;
  isLoadingAccept: boolean;
  isLoadingDecline: boolean;
  /** Prefixes the accept/decline/back/close test ids, e.g. `pause-offer`. */
  testIdPrefix: string;
};

/**
 * The chrome every retention offer shares: progress, the framing above the card,
 * one focused card, and the three-way footer that lets the customer go back, decline,
 * or take the offer.
 *
 * Each offer differs only in what fills the card, so keeping the chrome here is what
 * stops the steps from drifting apart — the flow already had two openings of different
 * widths once, and the swap between them read to customers as the modal overlapping
 * itself.
 */
export default function RetentionOfferShell(props: tRetentionOfferShellProps) {
  const {
    isOpen,
    heading,
    intro,
    cardIcon: CardIcon,
    cardTitle,
    cardTerm,
    children,
    cardFooter,
    footnote,
    onBack,
    onDecline,
    onAccept,
    acceptLabel,
    declineLabel,
    onClose,
    isLoadingAccept,
    isLoadingDecline,
    testIdPrefix,
  } = props;
  const { t } = useTranslation('settings/SubscriptionManager');
  const isLoading = isLoadingAccept || isLoadingDecline;

  return (
    <Modal
      open={isOpen}
      className="flex max-h-[92vh] w-11/12 max-w-[880px] flex-col overflow-hidden rounded-lg p-0"
    >
      <Button
        color="ghost"
        size="sm"
        shape="circle"
        className="absolute right-3 top-3 z-10"
        onClick={onClose}
        disabled={isLoading}
        aria-label={t('cancelSubscription.offer.close')}
        data-testid={`${testIdPrefix}-close-button`}
      >
        <XMarkIcon aria-hidden="true" className="h-5 w-5" />
      </Button>

      <Modal.Body className="flex flex-1 flex-col gap-5 overflow-y-auto px-8 py-6 font-sans">
        <header className="flex flex-shrink-0 flex-col items-center gap-2 text-center">
          <RetentionProgress current={3} className="justify-center pb-1" />
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t('cancelSubscription.offer.eyebrow')}
          </p>
          <h2 className="text-2xl font-bold leading-tight text-neutral-800">
            {heading}
          </h2>
          <p className="text-base text-neutral-600">{intro}</p>
        </header>

        <section className="flex-shrink-0 overflow-hidden rounded-lg border border-neutral-300">
          <div className="flex items-center gap-3 border-b border-neutral-300 bg-base-200 px-5 py-4">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <CardIcon aria-hidden="true" className="h-5 w-5 text-primary" />
            </span>
            {/* Title over terms, not title against a right-aligned term: the terms
                qualify the offer, so they read as its second line. */}
            <span className="flex flex-col">
              <span className="text-base font-semibold text-neutral-800">
                {cardTitle}
              </span>
              {cardTerm ? (
                <span className="text-sm text-neutral-600">{cardTerm}</span>
              ) : null}
            </span>
          </div>

          <div className="flex flex-col gap-4 p-5">{children}</div>

          {cardFooter ? (
            <div className="flex flex-col gap-1 bg-base-200 px-5 py-4">
              {cardFooter}
            </div>
          ) : null}
        </section>

        {footnote ? (
          <div className="flex flex-shrink-0 items-start gap-2 text-sm text-neutral-600">
            {footnote}
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Actions className="m-0 flex flex-shrink-0 flex-row items-center justify-end gap-3 border-t border-neutral-200 bg-base-100 px-8 py-4">
        <Button
          type="button"
          color="ghost"
          size="lg"
          className="mr-auto font-normal text-neutral-700"
          onClick={onBack}
          disabled={isLoading}
          data-testid={`${testIdPrefix}-back-button`}
        >
          <ArrowLeftIcon aria-hidden="true" className="mr-1 h-4 w-4" />
          {t('cancelSubscription.offer.backButton')}
        </Button>
        <form onSubmit={onDecline}>
          <Button
            type="submit"
            color="ghost"
            size="lg"
            className="font-normal text-neutral-700"
            loading={isLoadingDecline}
            disabled={isLoading}
            data-testid={`${testIdPrefix}-decline-button`}
          >
            {declineLabel}
          </Button>
        </form>
        <Button
          type="button"
          color="primary"
          size="lg"
          className="min-w-[180px] rounded-lg"
          loading={isLoadingAccept}
          disabled={isLoading}
          onClick={onAccept}
          data-testid={`${testIdPrefix}-accept-button`}
        >
          {acceptLabel}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
